import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { ThemedSelectOption } from './ThemedSelect';
import './ThemedSelect.css';
import './ThemedDropdown.css';

interface ThemedMultiSelectProps<T extends string> {
  values: T[];
  options: ThemedSelectOption<T>[];
  allLabel: string;
  onChange: (values: T[]) => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}

const ThemedMultiSelect = <T extends string>({
  values,
  options,
  allLabel,
  onChange,
  ariaLabel,
  disabled = false,
  className = '',
}: ThemedMultiSelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedValues = useMemo(() => new Set(values), [values]);
  const selectedLabel = values.length === 0
    ? allLabel
    : options.filter((option) => selectedValues.has(option.value)).map((option) => option.label).join(', ');

  useEffect(() => {
    const close = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const toggle = (value: T) => {
    const next = selectedValues.has(value)
      ? values.filter((selectedValue) => selectedValue !== value)
      : [...values, value];
    onChange(next.length === options.length ? [] : next);
  };

  return (
    <div className={`themed-select themed-multi-select ${className}`} ref={ref}>
      <button
        type="button"
        className="themed-select-trigger"
        onClick={() => setOpen((current) => !current)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-open={open || undefined}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <ChevronDown className={open ? 'open' : ''} size={18} aria-hidden />
      </button>
      {open && (
        <div
          className="themed-select-options themed-dropdown-menu"
          role="listbox"
          aria-label={ariaLabel}
          aria-multiselectable="true"
        >
          <button
            type="button"
            role="option"
            aria-selected={values.length === 0}
            className={`themed-select-option themed-multi-select-option themed-dropdown-option ${values.length === 0 ? 'themed-dropdown-option-active' : ''}`}
            onClick={() => onChange([])}
          >
            <span className="themed-multi-select-check" aria-hidden>
              {values.length === 0 && <Check size={14} strokeWidth={2.5} />}
            </span>
            <span>{allLabel}</span>
          </button>
          {options.map((option) => {
            const selected = selectedValues.has(option.value);
            return (
              <button
                type="button"
                role="option"
                aria-selected={selected}
                className={`themed-select-option themed-multi-select-option themed-dropdown-option ${selected ? 'themed-dropdown-option-active' : ''}`}
                key={option.value}
                disabled={option.disabled}
                onClick={() => toggle(option.value)}
              >
                <span className="themed-multi-select-check" aria-hidden>
                  {selected && <Check size={14} strokeWidth={2.5} />}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ThemedMultiSelect;
